/**
 * 공지사항 등록/수정 모달
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Notice,
    CreateNoticeRequestDto,
    UpdateNoticeRequestDto,
    TargetType,
    TargetScope,
    TARGET_TYPE_OPTIONS,
    TARGET_SCOPE_OPTIONS,
    POPUP_YN_OPTIONS,
    USE_YN_OPTIONS,
    URGENT_YN_OPTIONS,
    SUPPLIER_STATUS_SORT_ORDER,
    SUPPLIER_STATUS_CODES,
    ACTIVE_RIDER_STATUSES
} from '../../types/notice.types';
import { format } from 'date-fns';
import { getItem } from "@/shared/utils/storage";
import { supplierApi } from "@/features/partner/supplierStatus/api/SupplierApi";
import { getSupplierStatusLabel } from "@/shared/utils/labelConverterHelpers";
import { riderStatusApi } from "@/features/rider/riderStatus/api/riderStatusApi";

/** 협력사 옵션 타입 (상태 정보 포함) */
interface SupplierOption {
    value: string;
    label: string;
    statusCode: string;
    status: string;
    isOperating: boolean;
}

/** 라이더 옵션 타입 (협력사 정보 포함) */
interface RiderOption {
    value: string;
    label: string;
    riderId: string;
    riderName: string;
    suppId: string;
    suppName: string;
    status: string;
    isActive: boolean;
}

interface NoticeModalProps {
    notice: Notice | null;
    onSave: (data: CreateNoticeRequestDto | UpdateNoticeRequestDto) => void;
    onClose: () => void;
    loading: boolean;
}

export const NoticeModal: React.FC<NoticeModalProps> = ({
    notice,
    onSave,
    onClose,
    loading
}) => {
    const isEdit = !!notice;

    // 현재 로그인한 사용자 타입
    const userType = getItem<string>('USER_TYPE');
    const isSupplierUser = userType === 'SUPPLIER' || userType === 'S';

    const [formData, setFormData] = useState<{
        title: string;
        content: string;
        targetType: TargetType;
        targetScope: TargetScope;
        targetIds: string[];
        startDate: string;
        endDate: string;
        popupYn: string;
        urgentYn: string;
        useYn: string;
    }>({
        title: '',
        content: '',
        targetType: isSupplierUser ? 'R' : 'A',
        targetScope: 'ALL',
        targetIds: [],
        startDate: '',
        endDate: '',
        popupYn: 'Y',
        urgentYn: 'N',
        useYn: 'Y'
    });

    // 협력사/라이더 옵션 목록
    const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);
    const [riderOptions, setRiderOptions] = useState<RiderOption[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    // 검색어 상태
    const [searchKeyword, setSearchKeyword] = useState('');

    // 라이더 필터용 협력사 선택
    const [riderFilterSuppId, setRiderFilterSuppId] = useState<string>('');

    // 협력사 목록 로드 (상태 순서: 운영중 > 계약종료 접수 > 준비중 > 운영중지 > 계약종료)
    const loadSuppliers = useCallback(async () => {
        try {
            setOptionsLoading(true);
            const response = await supplierApi.getSupplierDetailList();
            const suppliers = response.suppliers ?? [];

            // 모든 협력사를 표시하고 상태 순서에 따라 정렬
            const options: SupplierOption[] = suppliers
                .filter(s => s.suppId && s.suppName)
                .map(s => {
                    const statusCode = s.statusCode || '';
                    const statusLabel = getSupplierStatusLabel(statusCode);
                    return {
                        value: s.suppId,
                        label: s.suppName,
                        statusCode: statusCode,
                        status: statusLabel,
                        isOperating: statusCode === SUPPLIER_STATUS_CODES.OPERATING,
                    };
                })
                .sort((a, b) => {
                    // 상태 코드 순서로 정렬 (운영중 > 계약종료 접수 > 준비중 > 운영중지 > 계약종료)
                    const orderA = SUPPLIER_STATUS_SORT_ORDER[a.statusCode] ?? 999;
                    const orderB = SUPPLIER_STATUS_SORT_ORDER[b.statusCode] ?? 999;
                    if (orderA !== orderB) return orderA - orderB;
                    // 같은 상태 내에서는 이름순 정렬
                    return a.label.localeCompare(b.label);
                });

            setSupplierOptions(options);
        } catch (error) {
            console.error('협력사 목록 로드 실패:', error);
            setSupplierOptions([]);
        } finally {
            setOptionsLoading(false);
        }
    }, []);

    // 라이더 목록 로드 (운영 중/계약 중 상태만, 협력사명 포함)
    const loadRiders = useCallback(async () => {
        try {
            setOptionsLoading(true);
            const response = await riderStatusApi.getRiderList();
            const riders = Array.isArray(response) ? response : [];

            // 운영 중/계약 중 상태인 라이더만 필터링
            const options: RiderOption[] = riders
                .filter(r => {
                    if (!r.riderId || !r.name) return false;
                    const statusName = r.contractStatusName || r.status?.description || '';
                    return ACTIVE_RIDER_STATUSES.includes(statusName);
                })
                .map(r => {
                    const statusName = r.contractStatusName || r.status?.description || '';
                    const supplierName = r.suppName || r.centerName || '-';
                    return {
                        value: r.riderId!,
                        label: `${r.riderId} (${r.name}) (${supplierName})`,
                        riderId: r.riderId!,
                        riderName: r.name!,
                        suppId: r.suppId || '',
                        suppName: supplierName,
                        status: statusName,
                        isActive: statusName === '운영 중'
                    };
                });

            setRiderOptions(options);
        } catch (error) {
            console.error('라이더 목록 로드 실패:', error);
            setRiderOptions([]);
        } finally {
            setOptionsLoading(false);
        }
    }, []);

    // 대상 타입이나 범위 변경 시 해당 목록 로드
    useEffect(() => {
        const needsSupplierList = formData.targetScope === 'INDIVIDUAL' &&
            (formData.targetType === 'S' || formData.targetType === 'SR');
        const needsRiderList = formData.targetScope === 'INDIVIDUAL' && formData.targetType === 'R';

        if (needsSupplierList) {
            loadSuppliers();
        }
        if (needsRiderList) {
            loadSuppliers(); // 라이더 필터용 협력사 목록도 로드
            loadRiders();
        }
    }, [formData.targetType, formData.targetScope, loadSuppliers, loadRiders]);

    // 수정 모드일 때 기존 데이터 로드
    useEffect(() => {
        if (notice) {
            setFormData({
                title: notice.title,
                content: notice.content,
                targetType: notice.targetType,
                targetScope: notice.targetScope,
                targetIds: notice.targetIds ?? [],
                startDate: notice.startDate ? format(notice.startDate, "yyyy-MM-dd'T'HH:mm") : '',
                endDate: notice.endDate ? format(notice.endDate, "yyyy-MM-dd'T'HH:mm") : '',
                popupYn: notice.popupYn,
                urgentYn: notice.urgentYn ?? 'N',
                useYn: notice.useYn
            });
        }
    }, [notice]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === 'targetType') {
            setFormData(prev => ({ ...prev, [name]: value as TargetType, targetIds: [], targetScope: 'ALL' }));
            setSearchKeyword('');
            setRiderFilterSuppId('');
            return;
        }

        if (name === 'targetScope') {
            setFormData(prev => ({ ...prev, [name]: value as TargetScope, targetIds: [] }));
            setSearchKeyword('');
            setRiderFilterSuppId('');
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            alert('제목을 입력해주세요.');
            return;
        }

        if (!formData.content.trim()) {
            alert('내용을 입력해주세요.');
            return;
        }

        // 개별 선택인데 대상을 선택하지 않은 경우 (전체, 라이더 제외)
        const needsSelection = formData.targetScope === 'INDIVIDUAL' &&
            (formData.targetType === 'S' || formData.targetType === 'SR' || formData.targetType === 'R');
        if (needsSelection && formData.targetIds.length === 0) {
            alert('대상을 선택해주세요.');
            return;
        }

        const data = {
            ...(isEdit && { noticeId: notice!.noticeId }),
            title: formData.title,
            content: formData.content,
            targetType: formData.targetType,
            targetScope: formData.targetScope,
            targetIds: formData.targetScope === 'INDIVIDUAL' ? formData.targetIds : undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined,
            popupYn: formData.popupYn,
            urgentYn: isSupplierUser ? 'N' : formData.urgentYn,
            useYn: formData.useYn
        };

        onSave(data);
    };

    // 협력사 사용자가 선택 가능한 대상 타입 옵션
    const availableTargetTypeOptions = useMemo(() => {
        if (isSupplierUser) {
            return TARGET_TYPE_OPTIONS.filter(opt => opt.value === 'R');
        }
        return TARGET_TYPE_OPTIONS;
    }, [isSupplierUser]);

    // 대상 범위 옵션 (대상 타입에 따라 다름)
    const availableTargetScopeOptions = useMemo(() => {
        // 전체(A), 라이더(R) 타입은 범위 선택 불가 (항상 전체)
        if (formData.targetType === 'A' || formData.targetType === 'R') {
            return [{ value: 'ALL', label: '전체' }];
        }
        return TARGET_SCOPE_OPTIONS;
    }, [formData.targetType]);

    // 필터링된 협력사 옵션
    const filteredSupplierOptions = useMemo(() => {
        // 이미 선택된 항목 제외
        const notSelected = supplierOptions.filter(opt => !formData.targetIds.includes(opt.value));

        if (!searchKeyword.trim()) {
            // 검색어 없으면 전체 표시 (상태 순서대로 정렬된 상태)
            return notSelected;
        }

        // 검색어가 있으면 운영 중 상태만 검색 가능
        return notSelected.filter(opt => {
            if (!opt.isOperating) return false; // 운영 중이 아니면 검색 결과에서 제외
            return opt.label.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                opt.value.toLowerCase().includes(searchKeyword.toLowerCase());
        });
    }, [supplierOptions, formData.targetIds, searchKeyword]);

    // 필터링된 라이더 옵션 (협력사 필터 적용)
    const filteredRiderOptions = useMemo(() => {
        let filtered = riderOptions.filter(opt => !formData.targetIds.includes(opt.value));

        // 협력사 필터 적용
        if (riderFilterSuppId) {
            filtered = filtered.filter(opt => opt.suppId === riderFilterSuppId);
        }

        // 검색어 필터 적용
        if (searchKeyword.trim()) {
            filtered = filtered.filter(opt =>
                opt.label.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                opt.riderId.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                opt.riderName.toLowerCase().includes(searchKeyword.toLowerCase())
            );
        }

        return filtered;
    }, [riderOptions, formData.targetIds, riderFilterSuppId, searchKeyword]);

    // 현재 선택에 따른 옵션 목록과 레이블
    const currentOptionsLabel = useMemo(() => {
        if (formData.targetType === 'S' || formData.targetType === 'SR') return '협력사';
        if (formData.targetType === 'R') return '라이더';
        return '';
    }, [formData.targetType]);

    // 선택된 항목 정보
    const selectedItems = useMemo(() => {
        if (formData.targetType === 'S' || formData.targetType === 'SR') {
            return supplierOptions.filter(opt => formData.targetIds.includes(opt.value));
        }
        if (formData.targetType === 'R') {
            return riderOptions.filter(opt => formData.targetIds.includes(opt.value));
        }
        return [];
    }, [formData.targetType, formData.targetIds, supplierOptions, riderOptions]);

    // 항목 추가/제거 핸들러
    const handleAddTarget = (value: string) => {
        if (!formData.targetIds.includes(value)) {
            setFormData(prev => ({ ...prev, targetIds: [...prev.targetIds, value] }));
        }
    };

    const handleRemoveTarget = (value: string) => {
        setFormData(prev => ({ ...prev, targetIds: prev.targetIds.filter(id => id !== value) }));
    };

    const handleSelectAll = () => {
        const currentFiltered = formData.targetType === 'R' ? filteredRiderOptions : filteredSupplierOptions;
        const allValues = currentFiltered.map(opt => opt.value);
        setFormData(prev => ({
            ...prev,
            targetIds: [...new Set([...prev.targetIds, ...allValues])]
        }));
    };

    const handleClearAll = () => {
        setFormData(prev => ({ ...prev, targetIds: [] }));
    };

    // 개별 선택이 필요한지 여부
    const showIndividualSelection = formData.targetScope === 'INDIVIDUAL' &&
        (formData.targetType === 'S' || formData.targetType === 'SR' || formData.targetType === 'R');

    // 현재 필터링된 옵션 목록
    const currentFilteredOptions = formData.targetType === 'R' ? filteredRiderOptions : filteredSupplierOptions;
    const currentTotalOptions = formData.targetType === 'R' ? riderOptions : supplierOptions;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{isEdit ? '공지사항 수정' : '공지사항 등록'}</h3>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>제목 <span className="required">*</span></label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="공지사항 제목을 입력하세요"
                                maxLength={100}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>내용 <span className="required">*</span></label>
                            <textarea
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                rows={6}
                                placeholder="공지사항 내용을 입력하세요"
                                required
                            />
                        </div>

                        {/* 대상 타입 안내 메시지 */}
                        {formData.targetType === 'A' && (
                            <div className="form-group">
                                <div className="alert alert-info" style={{
                                    background: '#dbeafe',
                                    border: '1px solid #3b82f6',
                                    borderRadius: '4px',
                                    padding: '12px 16px',
                                    color: '#1e40af'
                                }}>
                                    <strong>전체 공지</strong>는 모든 <strong>협력사</strong>와 <strong>라이더</strong>에게 전달됩니다.
                                    (운영 중, 계약 중 상태만 해당)
                                </div>
                            </div>
                        )}

                        {formData.targetType === 'SR' && (
                            <div className="form-group">
                                <div className="alert alert-info" style={{
                                    background: '#fef3c7',
                                    border: '1px solid #f59e0b',
                                    borderRadius: '4px',
                                    padding: '12px 16px',
                                    color: '#92400e'
                                }}>
                                    <strong>협력사 + 라이더</strong>: 선택한 협력사와 해당 협력사에 소속된 모든 라이더에게 동일한 내용이 전달됩니다.
                                </div>
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label>대상 타입 <span className="required">*</span></label>
                                <select
                                    name="targetType"
                                    value={formData.targetType}
                                    onChange={handleChange}
                                    disabled={isSupplierUser}
                                >
                                    {availableTargetTypeOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                {isSupplierUser && (
                                    <small className="form-hint">협력사 계정은 라이더에게만 공지를 보낼 수 있습니다.</small>
                                )}
                            </div>

                            <div className="form-group">
                                <label>대상 범위 <span className="required">*</span></label>
                                <select
                                    name="targetScope"
                                    value={formData.targetScope}
                                    onChange={handleChange}
                                    disabled={formData.targetType === 'A' || formData.targetType === 'R'}
                                >
                                    {availableTargetScopeOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                {(formData.targetType === 'A' || formData.targetType === 'R') && (
                                    <small className="form-hint">
                                        {formData.targetType === 'A' ? '전체 공지는 모든 대상에게 전달됩니다.' : '라이더 공지는 전체 라이더에게 전달됩니다.'}
                                    </small>
                                )}
                            </div>
                        </div>

                        {/* 개별 선택 시 대상 선택 UI */}
                        {showIndividualSelection && (
                            <div className="form-group">
                                <label>{currentOptionsLabel} 선택 <span className="required">*</span></label>
                                {optionsLoading ? (
                                    <div className="loading-text">목록을 불러오는 중...</div>
                                ) : currentTotalOptions.length === 0 ? (
                                    <div className="empty-text">선택 가능한 {currentOptionsLabel}가 없습니다.</div>
                                ) : (
                                    <div className="target-selector">
                                        {/* 선택된 항목 태그 영역 */}
                                        {selectedItems.length > 0 && (
                                            <div className="selected-tags">
                                                {selectedItems.map(item => (
                                                    <span key={item.value} className="tag">
                                                        {item.label}
                                                        <button
                                                            type="button"
                                                            className="tag-remove"
                                                            onClick={() => handleRemoveTarget(item.value)}
                                                        >
                                                            &times;
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* 라이더 선택 시 협력사 필터 */}
                                        {formData.targetType === 'R' && (
                                            <div className="rider-filter-wrapper" style={{ marginBottom: '8px' }}>
                                                <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
                                                    협력사로 필터링
                                                </label>
                                                <select
                                                    value={riderFilterSuppId}
                                                    onChange={(e) => setRiderFilterSuppId(e.target.value)}
                                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                                >
                                                    <option value="">전체 협력사</option>
                                                    {supplierOptions.map(opt => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label} ({opt.status})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* 검색 입력 */}
                                        <div className="search-input-wrapper">
                                            <input
                                                type="text"
                                                value={searchKeyword}
                                                onChange={(e) => setSearchKeyword(e.target.value)}
                                                placeholder={
                                                    formData.targetType === 'R'
                                                        ? '라이더 검색 (ID, 이름)'
                                                        : '협력사 검색 (운영 중 상태만 검색 가능)'
                                                }
                                                className="search-input"
                                            />
                                            {searchKeyword && (
                                                <button
                                                    type="button"
                                                    className="search-clear"
                                                    onClick={() => setSearchKeyword('')}
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </div>

                                        {/* 협력사 선택 시 안내 문구 */}
                                        {(formData.targetType === 'S' || formData.targetType === 'SR') && (
                                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', marginBottom: '8px' }}>
                                                * 운영 중 상태의 협력사만 검색이 가능합니다. 계약 중 협력사는 목록에서 직접 선택해주세요.
                                            </div>
                                        )}

                                        {/* 전체 선택/해제 버튼 */}
                                        <div className="select-actions">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline"
                                                onClick={handleSelectAll}
                                                disabled={currentFilteredOptions.length === 0}
                                            >
                                                {searchKeyword || riderFilterSuppId ? '검색결과 전체 선택' : '전체 선택'}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline"
                                                onClick={handleClearAll}
                                                disabled={formData.targetIds.length === 0}
                                            >
                                                전체 해제
                                            </button>
                                            <span className="select-count">
                                                선택: {formData.targetIds.length}개 / 전체: {currentTotalOptions.length}개
                                            </span>
                                        </div>

                                        {/* 선택 가능한 목록 */}
                                        <div className="options-list">
                                            {currentFilteredOptions.length === 0 ? (
                                                <div className="empty-text">
                                                    {searchKeyword || riderFilterSuppId
                                                        ? '검색 결과가 없습니다.'
                                                        : '모든 항목이 선택되었습니다.'}
                                                </div>
                                            ) : (
                                                currentFilteredOptions.map(opt => {
                                                    // 협력사와 라이더를 구분하여 active 상태 확인
                                                    const isActiveStatus = formData.targetType === 'R'
                                                        ? (opt as RiderOption).isActive
                                                        : (opt as SupplierOption).isOperating;
                                                    return (
                                                        <div
                                                            key={opt.value}
                                                            className={`option-item ${!isActiveStatus ? 'inactive' : ''}`}
                                                            onClick={() => handleAddTarget(opt.value)}
                                                        >
                                                            <span className="option-label">
                                                                {opt.label}
                                                                {'status' in opt && opt.status && (
                                                                    <span style={{
                                                                        marginLeft: '8px',
                                                                        fontSize: '11px',
                                                                        padding: '2px 6px',
                                                                        borderRadius: '4px',
                                                                        background: isActiveStatus ? '#dcfce7' : '#fef9c3',
                                                                        color: isActiveStatus ? '#166534' : '#854d0e'
                                                                    }}>
                                                                        {opt.status}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="option-add">+ 추가</span>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label>게시 시작</label>
                                <input
                                    type="datetime-local"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label>게시 종료</label>
                                <input
                                    type="datetime-local"
                                    name="endDate"
                                    value={formData.endDate}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>팝업 표시</label>
                                <select
                                    name="popupYn"
                                    value={formData.popupYn}
                                    onChange={handleChange}
                                >
                                    {POPUP_YN_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* 긴급 공지 - 본사 사용자만 설정 가능 */}
                            {!isSupplierUser && (
                                <div className="form-group">
                                    <label>긴급 공지</label>
                                    <select
                                        name="urgentYn"
                                        value={formData.urgentYn}
                                        onChange={handleChange}
                                    >
                                        {URGENT_YN_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    <small className="form-hint">긴급 공지는 빨간색으로 강조 표시됩니다.</small>
                                </div>
                            )}

                            {isEdit && (
                                <div className="form-group">
                                    <label>사용 여부</label>
                                    <select
                                        name="useYn"
                                        value={formData.useYn}
                                        onChange={handleChange}
                                    >
                                        {USE_YN_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            취소
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? '처리중...' : (isEdit ? '수정' : '등록')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
